import { useMemo } from 'react';
import { useAbility } from '@casl/react';
import { v4 as uuid } from 'uuid';
import { useTranslate } from '@bynder/localization';

import { IconListView, IconRestore, IconTime, IconUpload, IconUser, IconCart } from '@bynder/icons';
import { MenuItem } from '../../types';
import { AbilityContext } from '../../abilities/Can';
import { LogoutItem } from './LogoutItem';
import { LanguageMenu } from './LanguageMenu/LanguageMenu';
import { OutOfOfficeMenuItem } from '../OutOfOffice/OutOfOfficeMenuItem';

export function useUserMenuItems() {
	const ability = useAbility(AbilityContext);
	const { translate } = useTranslate();
	const showWorkspace = ability.can('show', 'access_workspace');

	const userMenuItems: MenuItem[] = useMemo(() => {
		const canShowUSerMenu = ability.can('show', 'user_settings_menu');

		if (!canShowUSerMenu) {
			return [];
		}

		const dataItems: MenuItem[] = [
			{
				isVisible: ability.can('select', 'menu_item_edit_profile'),
				href: '/user/profile/',
				icon: IconUser,
				label: translate('user.editProfile'),
			},
			{
				isVisible: ability.can('show', 'user_outofoffice'),
				href: '',
				label: '',
				Component: OutOfOfficeMenuItem,
			},
			{
				isVisible: ability.can('select', 'menu_item_fte_management'),
				href: '/workflow/availability/overview/',
				icon: IconTime,
				label: translate('workflow.FTEManagement'),
			},
			{
				isVisible: showWorkspace,
				href: '/workspace/',
				icon: IconUpload,
				label: translate('user.workspace'),
			},
			{
				isVisible: ability.can('select', 'menu_item_bare_uploader'),
				href: '/upload/mass/',
				icon: IconUpload,
				label: translate('upload.bareUploader'),
			},
			{
				isVisible: ability.can('select', 'menu_item_version_updater'),
				href: '/upload/version/',
				icon: IconRestore,
				label: translate('upload.versionUpdater'),
			},
			{
				isVisible: ability.can('select', 'menu_item_transfer'),
				href: '/transfer/share/',
				icon: IconListView,
				label: translate('transfer.history'),
			},
			{
				isVisible: true,
				href: '',
				label: '',
				Component: LanguageMenu,
			},
			{
				isVisible: ability.can('show', 'brandstore'),
				href: '/store/orders/list/',
				icon: IconCart,
				label: translate('brandstore.orderHistory'),
			},
			{
				isVisible: true,
				href: '',
				label: '',
				Component: LogoutItem,
			},
		];

		// adding keys
		return dataItems.map((item) => ({
			...item,
			key: uuid(),
		}));
	}, [ability, showWorkspace, translate]);

	return userMenuItems;
}
